import React from 'react';
import joi from 'joi';
import {
  $criteria,
  $ruleI18nAttributesRedirectUrlSchema,
  AccessImpactMode,
  AccessRuleI18nAttributes,
  ActionCondition,
  ActionTypes,
  RuleInput,
  RuleType,
} from '@loyalty/shared-common/rule-api-client';
import { AxiosError } from 'axios';
import { Button, Skeleton, Tooltip } from '@mui/material';
import { EventName, EventSource, Feature } from '@loyalty/shared-common';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormProvider, useForm } from 'react-hook-form';
import { QlfTitle, colorPalette, useAlerts } from '@qualifio/ui-commons';
import { faCheck } from '@fortawesome/pro-solid-svg-icons/faCheck';
import { faSpinner } from '@fortawesome/pro-solid-svg-icons/faSpinner';
import { makeStyles } from '@mui/styles';
import { useTranslation } from 'react-i18next';
import { AccessCriteriaForm } from '../../libs/access/components/detail/AccessCriteriaForm';
import { AccessRedirectForm } from '../../libs/access/components/detail/AccessRedirectForm';
import { AccessRuleFormValue } from '../../libs/earning-rules/form';
import { DetailForm } from '../../libs/shared/layout/DetailForm';
import { QlfMasterDetailContainer } from '../../libs/shared/layout/QlfMasterDetailContainer';
import { useApi } from '../../libs/shared/api-client/ApiClientProvider';
import { useCustomKeys } from '../../libs/custom-keys/CustomKeysContext';
import { useFeatures } from '../../libs/features/FeaturesContext';
import { useJoiResolver } from '../../libs/shared/forms/use-joi-resolver';
import { useProgram } from '../../libs/loyalty/ProgramContext';
import { useRules } from '../../libs/rules/RulesContext';

const useStyles = makeStyles(() => ({
  title: {
    textAlign: 'center',
    justifyItems: 'center',
  },
  description: {
    textAlign: 'center',
    marginBottom: '20px',
  },
  hrSeparation: {
    border: `1px solid ${colorPalette.grey.main}`,
  },
}));

const accessFormSchema = joi.object<AccessRuleFormValue>({
  criteria: $criteria,
  i18nAttributes: joi.object<AccessRuleI18nAttributes>().pattern(
    /[a-z]+/i,
    joi.object({
      redirectUrl: $ruleI18nAttributesRedirectUrlSchema,
    }),
  ),
});

export function Root() {
  const { t } = useTranslation();
  const classes = useStyles();
  const { accessRule, setAccessRule } = useRules();
  const { features } = useFeatures();
  const { write: accessWrite } = features[Feature.PROGRAM_ACCESS];
  const { program, setProgram } = useProgram();
  const { ruleApi, gamificationApi } = useApi();
  const { notify } = useAlerts();

  const form = useForm<AccessRuleFormValue>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      criteria: accessRule?.criteria || { and: [] },
      i18nAttributes: Object.fromEntries(
        program.languages.map(lang => [
          lang,
          { redirectUrl: accessRule?.i18nAttributes?.[lang]?.redirectUrl || '' },
        ]),
      ),
    },
    resolver: useJoiResolver(accessFormSchema),
  });

  const onError = (error: AxiosError) => {
    if (error.response?.status === 400) {
      notify(t('access.notify.enable.error'), 'error');
    } else {
      notify(t('access.notify.save.error'), 'error');
    }
  };

  const {
    formState: { dirtyFields, isDirty, isSubmitting },
    reset,
  } = form;
  const onSubmit = (accessRuleFormInput: AccessRuleFormValue) => {
    program.languages.forEach(language => {
      accessRuleFormInput.i18nAttributes[language].description = 'Access rule';
    });
    const ruleInput = {
      id: accessRule?.id,
      name: `Access rule`,
      eventSource: EventSource.QUALIFIO_LOYALTY,
      eventName: EventName.ACCESS,
      type: RuleType.ACCESS,
      i18nAttributes: accessRuleFormInput.i18nAttributes,
      criteria: accessRuleFormInput.criteria ?? null,
      actions: [
        {
          kind: ActionTypes.ACCESS,
          actionCondition: ActionCondition.HIT,
          impact: { mode: AccessImpactMode.GRANT_ACCESS },
        },
        {
          kind: ActionTypes.ACCESS,
          actionCondition: ActionCondition.MISS,
          impact: { mode: AccessImpactMode.REVOKE_ACCESS },
        },
      ],
      options: {},
      position: accessRule?.position,
      version: accessRule?.version,
      positionVersion: accessRule?.positionVersion,
    } as RuleInput;

    const upsertRule = () =>
      accessRule
        ? ruleApi.updateRule(accessRule.id, ruleInput)
        : ruleApi.addRule(program.id, ruleInput);

    const criteriaChanged =
      dirtyFields.criteria &&
      JSON.stringify(ruleInput.criteria) !== JSON.stringify(accessRule?.criteria);

    const shouldSaveRule = criteriaChanged || dirtyFields.i18nAttributes;

    (shouldSaveRule ? upsertRule() : Promise.resolve(accessRule))
      .then(updatedRule => {
        setAccessRule(updatedRule);
        reset({
          criteria: updatedRule?.criteria,
          i18nAttributes: updatedRule?.i18nAttributes as any,
        });
        notify(t('shared.actions.saved'), 'success');
        return gamificationApi.markProgramForRefresh(program.id);
      })
      .catch(onError);
  };

  return (
    <QlfMasterDetailContainer>
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit, console.error)} style={{ height: '100%' }}>
          <DetailForm>
            <DetailForm.Form>
              <div className={classes.title}>
                <QlfTitle variant="h2" separator={{ state: 'center' }}>
                  {t('access-rule.edition.title')}
                </QlfTitle>
              </div>
              <div className={classes.description}>{t('access-rule.edition.description')}</div>
              <AccessCriteriaForm />
              <hr className={classes.hrSeparation} />
              <AccessRedirectForm type={RuleType.ACCESS} />
            </DetailForm.Form>

            <DetailForm.Footer>
              <Tooltip
                title={accessWrite ? '' : t('shared.action.permission.required')}
                placement="left"
              >
                <div>
                  <Button
                    type="submit"
                    startIcon={
                      <FontAwesomeIcon
                        type="submit"
                        fontSize="inherit"
                        icon={isSubmitting ? faSpinner : faCheck}
                        spin={isSubmitting}
                      />
                    }
                    color="success"
                    disabled={!isDirty || isSubmitting || !accessWrite}
                  >
                    {t('currencies.save.button')}
                  </Button>
                </div>
              </Tooltip>
            </DetailForm.Footer>
          </DetailForm>
        </form>
      </FormProvider>
    </QlfMasterDetailContainer>
  );
}

export function AccessPage() {
  const { isLoading: isLoadingRules } = useRules();
  const { isLoading: isLoadingCustomKeys } = useCustomKeys();

  const isLoading = isLoadingRules('fetchRules') || isLoadingCustomKeys('getCustomKeys');

  return isLoading ? <Skeleton /> : <Root />;
}
